import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Lock, Eye, Database, Bell, UserCheck, Wallet } from "lucide-react";
import { usePlatform } from "@/context/PlatformContext";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import PublicHeader from "@/components/PublicHeader";

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { settings } = usePlatform();
  const appTitle = settings?.title || "GEMS";

  return (
    <div className="min-h-screen bg-[#fdfdfd] text-slate-900 flex flex-col justify-between">
      <SEO
        title={`Privacy Policy — ${appTitle}`}
        description={`Learn how ${appTitle} protects your account data, financial ledger entries, privacy, and personal information.`}
        keywords="privacy policy, data protection, ledger security, privacy statement, expense data security"
        canonicalUrl="https://splitly.app/privacy-policy"
        schemaType="WebPage"
      />
      {/* Top Header */}
      <PublicHeader showBack={true} />

      {/* Main Content Area */}
      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-12 flex-1 w-full">
        {/* Banner */}
        <div className="mb-10 text-center sm:text-left">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200/80 text-xs font-semibold text-emerald-700 mb-4">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            Privacy & Data Security
          </div>
          <h1 className="heading text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
            Privacy Policy
          </h1>
          <p className="mt-3 text-slate-500 text-sm">
            Last updated: August 27, 2026 • Effective immediately
          </p>
        </div>

        {/* Card Body */}
        <div className="bg-white rounded-3xl p-8 sm:p-12 border border-slate-200/70 card-soft space-y-8 text-slate-700 text-sm leading-relaxed">
          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center">
                <Lock className="w-4 h-4" />
              </div>
              1. Overview & Commitment
            </div>
            <p>
              At <strong>{appTitle}</strong>, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our group-expense management platform. By accessing or using {appTitle}, you consent to the practices described in this policy.
            </p>
          </section>

          <hr className="border-slate-100" />

          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-blue-100 text-blue-700 grid place-items-center">
                <Database className="w-4 h-4" />
              </div>
              2. Information We Collect
            </div>
            <p className="mb-3">We collect personal and financial transaction data to provide accurate group expense tracking:</p>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li><strong>Account Data:</strong> Name, email address, avatar photo, and authentication credentials.</li>
              <li><strong>Expense & Group Data:</strong> Expense amounts, descriptions, group names, member assignments, settlement records, and categories.</li>
              <li><strong>Technical Data:</strong> IP addresses, browser types, device identifiers, and system interaction logs required for auditing and security.</li>
            </ul>
          </section>

          <hr className="border-slate-100" />

          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-purple-100 text-purple-700 grid place-items-center">
                <Eye className="w-4 h-4" />
              </div>
              3. How We Use Your Data
            </div>
            <p className="mb-3">Your information is used strictly to power core app functionalities:</p>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li>Calculating group balances, debt settlements, and financial ledger entries.</li>
              <li>Sending transactional notifications regarding added expenses or settlement requests.</li>
              <li>Preventing fraudulent activity and enforcing access control for group administrators.</li>
              <li>We <strong>never</strong> sell or rent your personal information to third-party advertisers.</li>
            </ul>
          </section>

          <hr className="border-slate-100" />

          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-700 grid place-items-center">
                <Bell className="w-4 h-4" />
              </div>
              4. Data Protection & Security
            </div>
            <p>
              We implement industry-standard encryption protocols (TLS/SSL) for data in transit and stored records. All financial balances are derived dynamically from an append-only transaction ledger, ensuring full auditability and data integrity.
            </p>
          </section>

          <hr className="border-slate-100" />

          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-rose-100 text-rose-700 grid place-items-center">
                <UserCheck className="w-4 h-4" />
              </div>
              5. Your Rights & Data Export
            </div>
            <p>
              You maintain full control over your personal data. You may update your profile, request export of your group transaction records, or request complete account deletion by reaching out to our support team at{" "}
              <a
                href="https://saharey.com/contactus"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-900 font-semibold underline underline-offset-2 hover:text-emerald-600"
              >
                Saharey Contact Support
              </a>.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
